import React, { useState } from 'react';
import { Button, HStack, Text, VStack } from '@adc/parallax-component-library';
import * as Yup from 'yup';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import './Modal.scss';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import TextInputWithError from '../ui/forms/TextInputWithError';
import MessageBanner from '../messageBanner/MessageBanner';
import { Account, AccountCreationRequest } from '../../models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  accountsQueryOptions,
  createAccountMutateOptions,
} from '../../queryOptions';
import mediator from '../../utilities/mediator';
import PasswordInputWithError from '../ui/forms/PasswordInputWithError';
import { useAppSelector } from '../../reducers/hooks';
import { generateYupPasswordSchema } from '../../services/auth';

type Props = {
  trigger: React.ReactNode;
};

/**
 * Modal for creating a new user account. Handles account creation internally, and
 * automatically sends an account invitation email when account creation is successful.
 * @param {React.ReactNode} trigger - The element that triggers the modal.
 * @returns {React.JSX.Element}
 */
export function CreateAccountModal({ trigger }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const queryClient = useQueryClient();

  const { passwordRules } = useAppSelector((state) => state.config);

  const createAccount = useMutation(
    createAccountMutateOptions({
      onSuccess: (account: Account) =>
        queryClient.setQueryData(
          accountsQueryOptions().queryKey,
          (accounts: Account[] | undefined) => [...(accounts ?? []), account],
        ),
    }),
  );

  const initialValues: AccountCreationRequest = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  };

  const validationSchema = Yup.object()
    .shape({
      firstName: Yup.string().required(i18n.t('Form.genericRequired')),
      lastName: Yup.string().required(i18n.t('Form.genericRequired')),
      email: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .email(i18n.t('Form.emailInvalid')),
      password: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .min(8, i18n.t('Form.passwordMinLength')),
      confirmPassword: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .oneOf([Yup.ref('password')], i18n.t('Form.passwordsDoNotMatch')),
    })
    .concat(generateYupPasswordSchema(passwordRules));

  const onClose = (helpers: FormikHelpers<AccountCreationRequest>) => {
    helpers.resetForm();
    setIsOpen(false);
    setErrorMessage('');
  };

  const onSubmit = async (
    values: AccountCreationRequest,
    helpers: FormikHelpers<AccountCreationRequest>,
  ) => {
    const createAccountPromise = createAccount
      .mutateAsync({
        account: values,
      })
      .catch((err) => {
        if (err.status === 409) {
          setErrorMessage('Modal_CreateAccount.error_duplicate_email');
        } else {
          setErrorMessage('Modal_CreateAccount.error_generic');
        }
      });

    // TODO: Remove flag after CSP-2189 is complete
    if (window.enableIncompleteFeatures) {
      return createAccountPromise
        .then((account) => {
          if (account) {
            return mediator.request<Account>('account:sendInvite', {
              key: account.key,
            });
          }
        })
        .then((updatedAccount: Account | void) => {
          if (updatedAccount) {
            queryClient.setQueryData(
              accountsQueryOptions().queryKey,
              (accounts: Account[] | undefined) =>
                accounts?.map((account) =>
                  account.key === updatedAccount.key ? updatedAccount : account,
                ) || [],
            );
            onClose(helpers);
          }
        })
        .catch(() => {
          setErrorMessage('Modal_CreateAccount.error_user_invite');
        });
    }

    return createAccountPromise.then(() => onClose(helpers));
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <HStack justifyContent="space-between">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_CreateAccount.title')}
              </Text>
            </Dialog.Title>
          </HStack>

          {errorMessage ? (
            <VStack paddingVertical={'$4'}>
              <MessageBanner
                bodyText={i18n.t(errorMessage)}
                type="danger"
                testID="error-banner"
              />
            </VStack>
          ) : null}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<AccountCreationRequest>) => (
              <Form id="create-account-form" noValidate>
                <VStack gap="$2" paddingVertical="$2">
                  <HStack gap="$4">
                    <VStack flex={1}>
                      <TextInputWithError
                        label={i18n.t('Account.firstName')}
                        name="firstName"
                        testID="first-name"
                      />
                    </VStack>
                    <VStack flex={1}>
                      <TextInputWithError
                        label={i18n.t('Account.lastName')}
                        name="lastName"
                        testID="last-name"
                      />
                    </VStack>
                  </HStack>

                  <TextInputWithError
                    label={i18n.t('Account.email')}
                    name="email"
                    testID="email"
                  />

                  <HStack gap="$4">
                    <VStack flex={1}>
                      <PasswordInputWithError
                        name="password"
                        label={i18n.t('Account.password')}
                        testID="password"
                        autocomplete="new-password"
                      />
                    </VStack>
                    <VStack flex={1}>
                      <PasswordInputWithError
                        name="confirmPassword"
                        label={i18n.t('Account.confirmPassword')}
                        testID="confirm-password"
                        autocomplete="new-password"
                      />
                    </VStack>
                  </HStack>

                  <VStack alignSelf="flex-start">
                    <Text type={'$labelBase.default'} textAlign="left">
                      {i18n.t('PasswordRequirements.passwordRequirements')}
                    </Text>
                    {passwordRules.requirements.map((req) => (
                      <Text
                        type={'$labelBase.default'}
                        key={req}
                        marginLeft={'$2'}
                        textAlign="left"
                      >
                        {`\u2022 ${i18n.t(`PasswordRequirements.${req}`, {
                          min: passwordRules.rules.min?.value,
                          max: passwordRules.rules.max?.value,
                          count: passwordRules.rules.count?.value || 1,
                        })}`}
                      </Text>
                    ))}
                  </VStack>
                </VStack>

                <HStack justifyContent="flex-end" gap="$2">
                  <Button
                    variant="secondary"
                    label={i18n.t('Common.cancel')}
                    aria-label={i18n.t('Common.cancel')}
                    testID="cancel"
                    disabled={formikProps.isSubmitting}
                    onPress={() => onClose(formikProps)}
                  />
                  <Button
                    variant="primary"
                    onPress={formikProps.submitForm}
                    label={i18n.t('Common.save')}
                    testID="save-account"
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                  />
                </HStack>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
